// Day 9 Day 2 followup — auto-enable entitled imagery for restricted operators.
//
// Background: there is no default-imagery activation pathway for normal users.
// They land on the map with an empty imagery stack and click acquisition pills
// in LayerPanel → ImageryTab to populate it. Restricted operators don't see
// LayerPanel (PadRekko-only shell), so they need an automatic activation here
// or the tile pipeline never fires — operating_aoi is set, but no imagery
// layer ever lands in useLayerRegistryStore for useSyncRegistryLayers to act on.
//
// Activation pipeline once stack entries exist:
//   addLayer → _reconcile → useLayerRegistryStore.registerLayers
//   → useSyncRegistryLayers picks them up → /tile/session fires
//   → tile URL carries operating_aoi (Item #6) → TiTiler cutline clips
//
// Trigger conditions (all must be true):
//   - isRestricted (only restricted users get auto-enable)
//   - activeOperatingAoiId set (Item #6 effect has run)
//   - catalog not still loading (avoid acting on stale series list)
//   - stack is empty (defense-in-depth: don't fight a saved layer set
//     restored by useLayerSetsStore — its addLayer calls would have
//     populated stack already by the time this effect runs)
//
// Empty catalog: warn but don't error. A demo-prep mistake (dataset
// footprints don't intersect the operator's AOI) shows up as no imagery
// rendering; the warn makes the root cause discoverable in devtools.

import { useEffect } from "react";

import { useAuthStore, selectIsRestrictedOperator } from "../stores/useAuthStore";
import { useOperatingAoiContext } from "../stores/useOperatingAoiContext";
import { useImageryStackStore } from "../stores/useImageryStackStore";

export function useRestrictedAutoEnableImagery() {
  const isRestricted = useAuthStore(selectIsRestrictedOperator);
  const activeOperatingAoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);
  const catalogSeries = useImageryStackStore((s) => s.catalog.series);
  const catalogLoading = useImageryStackStore((s) => s.loading);
  const stackLength = useImageryStackStore((s) => s.stack.length);
  const addLayer = useImageryStackStore((s) => s.addLayer);

  useEffect(() => {
    if (!isRestricted) return;
    if (!activeOperatingAoiId) return;
    if (catalogLoading) return;
    if (stackLength > 0) return;

    if (!catalogSeries?.length) {
      console.warn(
        "[restricted-auto-enable] No entitled imagery for operator's AOI — map will show no imagery layers"
      );
      return;
    }

    for (const series of catalogSeries) {
      // Register entitled imagery but DON'T reveal it here. For restricted
      // operators, imagery visibility is owned by useRestrictedPadDrivenImagery,
      // which reveals layers only while a pad is being inspected (high zoom →
      // cheap cutline). Adding them visible:true would request low-zoom cutline
      // tiles at the operating-AOI overview and 502/504 on cold start. This hook
      // REGISTERS; the pad-driven hook CONTROLS VISIBILITY.
      addLayer(series.series_key, { visible: false });
    }
  }, [
    isRestricted,
    activeOperatingAoiId,
    catalogSeries,
    catalogLoading,
    stackLength,
    addLayer,
  ]);
}
